// Saved-card consent copy.
//
// ===========================================================================
// WHY THERE ARE TWO VERSIONS IN THIS FILE
// ===========================================================================
// v2 said that saving a card AUTHORISES us to charge it automatically after
// every completed cleaning and for approved price adjustments. That sentence is
// now false by design. Authorisation to charge an absent client comes from ONE
// place and one place only — the auto-payment sign-up (autoPay.js / the Account
// switch, recorded server-side in routes/services/autoPayConsent.js). Saving a
// card stores a token so the client can check out faster; it decides nothing
// about being charged. Repeating an auto-charge mandate on the add-card
// checkbox told clients they had agreed to something they had not, which is the
// exact confusion the auto-payment separation exists to end (owner, 2026-07-19:
// "by adding a payment method they don't authorize us; they only authorize us
// if they turn on auto payments").
//
// v3 therefore says the thing the client actually needs to know at that moment:
// Holiday Home Cleaning does not hold their card details — the payment gateway
// does, tokenised.
//
// PER routes/services/cardConsent.js: the copy is NEVER edited in place. v2 is
// kept verbatim below so every historical consent stamped 'v2-2026-07' on
// client_payment_methods stays reconstructable. New consents render v3.
//
// ===========================================================================
// >>> DEPLOY COUPLING — READ BEFORE SHIPPING <<<
// ===========================================================================
// The BACKEND stamps the version on client_payment_methods (routes/cards.js and
// routes/payments.js read CARD_CONSENT_VERSION from
// routes/services/cardConsent.js). VERIFIED 2026-07-19: backend, this file and
// the mobile app (hooks/clientHooks/useClientCards.ts) are all on 'v3-2026-07'
// with BYTE-IDENTICAL text. (The note here previously said the backend was
// still on v2 — that was stale; the bump has shipped.)
//
// THREE-WAY COUPLING: the version + text pair is an audit record of what a
// client agreed to, so the text may never be edited on one surface alone — not
// even to shorten it for layout. Any wording change is a coordinated v4 bump
// across backend + portal + mobile in the same deploy, with v3 kept below
// verbatim exactly as v2 is. If the copy does not fit its container, change the
// CONTAINER.
// ===========================================================================

// ── HISTORICAL. Do not render. Kept so stamped v2 consents resolve. ─────────
export const CARD_CONSENT_VERSION_V2 = 'v2-2026-07';

export const CARD_CONSENT_TEXT_V2 =
    'You authorise Holiday Home Cleaning to charge this card automatically after ' +
    'each completed cleaning, and for approved price adjustments (including after ' +
    "3 days if you don't respond). Amounts follow your rate card. Remove the card " +
    'anytime from your Account.';

export const CARD_CONSENT_VERSION_V3 = 'v3-2026-07';

export const CARD_CONSENT_TEXT_V3 =
    'Save this card for faster checkout. Holiday Home Cleaning does not store your '
    + 'card details — they are held securely as a token by our payment gateway '
    + 'provider, and we only ever see the card type and the last four digits. Saving '
    + 'a card does not authorise any charge: you start every payment yourself unless '
    + 'you separately switch on automatic payments. Remove the card anytime from your '
    + 'Account.';

// ── CURRENT. This is what the add-card form shows. ─────────────────────────
// v3 → v4 (2026-07-19). v3 spelled out that saving a card does not sign the
// client up for automatic payments. Owner: "no need to mention this under the
// label" — the sign-up control sits in the SAME form, so the sentence explained
// something already on screen. What remains is only what the client cannot see
// for themselves: where the card actually lives.
export const CARD_CONSENT_VERSION = 'v4-2026-07';

// Note what this text does NOT contain: any permission to charge. It is a
// storage disclosure. If a future edit reintroduces "you authorise… charge…"
// here, the auto-payment sign-up has been quietly bypassed.
export const CARD_CONSENT_TEXT =
    'Save this card for faster checkout. Holiday Home Cleaning does not store your '
    + 'card details — they are held securely as a token by our payment gateway '
    + 'provider, and we only ever see the card type and the last four digits. '
    + 'Remove the card anytime from your Account.';

// Resolve the copy a stored consent version refers to (audit / admin display).
// Unknown versions return null rather than guessing — a wrong answer about what
// someone agreed to is worse than no answer.
export const cardConsentTextForVersion = (version) => {
    if (version === CARD_CONSENT_VERSION) return CARD_CONSENT_TEXT;
    if (version === CARD_CONSENT_VERSION_V3) return CARD_CONSENT_TEXT_V3;
    if (version === CARD_CONSENT_VERSION_V2) return CARD_CONSENT_TEXT_V2;
    return null;
};
